import { useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { hasShownStoreChangeDialog } from 'features/store/constants';

import { ILocation } from '@rbi-ctg/frontend';
import { ServiceMode } from 'generated/graphql-gateway';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { useOrderContext } from 'state/order';
import { useServiceModeLive } from 'state/service-mode';
import { routes } from 'utils/routing';

import { UseStoreLocatorWithMapReturn } from '../../types';
import { useStoreChangeDialog } from '../change-store-confirmation-dialog/use-store-change-dialog';

export const useStoreLocatorWithMap = (): UseStoreLocatorWithMapReturn => {
  const { deliveryAddress } = useOrderContext();
  const serviceMode = useServiceModeLive();
  const { noStoreSelected, store: restaurant } = useStoreContext();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const router = useRouter();

  const handleNavigateToStoreLocator = () => {
    router.navigate({
      pathname: isDelivery ? routes.deliveryAddress : routes.storeLocator,
      params: {
        redirectUrl: routes.cartCheckout,
      },
    });
    hasShownStoreChangeDialog.current = true;
  };

  const storeChangeDialogProps = useStoreChangeDialog(handleNavigateToStoreLocator);

  const { distanceText, isLoading: isLoadingDistance, canShowDistance } = useDistanceText({
    restaurant,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const addressLine1 = isDelivery
    ? deliveryAddress?.addressLine1 || ''
    : restaurant?.physicalAddress?.address1 || '';

  const cityStateZip = isDelivery
    ? `${deliveryAddress?.city || ''}, ${deliveryAddress?.state || ''} ${
        deliveryAddress?.zip || ''
      }`
    : `${restaurant?.physicalAddress?.city || ''}, ${
        restaurant?.physicalAddress?.stateProvince || ''
      } ${restaurant?.physicalAddress?.postalCode || ''}`;

  const restaurantCoordinates: ILocation | undefined = useMemo(
    () =>
      restaurant.latitude && restaurant.longitude
        ? {
            lat: restaurant.latitude,
            lng: restaurant.longitude,
          }
        : undefined,
    [restaurant.latitude, restaurant.longitude]
  );

  return {
    noStoreSelected,
    addressLine1,
    cityStateZip,
    handleChangeStore: storeChangeDialogProps.openDialog,
    isLoadingDistance,
    distanceText,
    canShowDistance,
    restaurantCoordinates,
    dialogProps: storeChangeDialogProps,
  };
};
